import {
    createRouter,
    createWebHistory,
    type NavigationGuardWithThis,
    type RouteLocationNormalized,
    type RouteRecordRaw,
} from 'vue-router'

import { useSessionStore } from '@/application/auth'

/**
 * Router Vue. Sprint 8-B introduit l'auth :
 *   - `/login`            : public (formulaire local + bouton SSO)
 *   - `/showcase`         : public (référence visuelle Sprint 8-A)
 *   - `/`                 : authentifié (HomeView placeholder Sprint 8-B)
 *
 * Les routes "publiques" sont marquées `meta.public: true`. Toutes les
 * autres déclenchent le guard `requireAuth` qui :
 *   1. attend la résolution initiale (`status === 'unknown'`) en
 *      déléguant à l'AppHydrate exécuté au boot (cf. main.ts)
 *   2. redirige vers `/login?redirect=<path original>` si pas auth
 */

const routes: RouteRecordRaw[] = [
    {
        path: '/',
        name: 'dashboard',
        component: () => import('@/presentation/views/DashboardView.vue'),
    },
    // Admin CRUD pages. Le menu sidebar PAppShell filtre par rôle ; le
    // scope tenant est forcé côté backend pour les non-SuperAdmin.
    {
        path: '/admin/api-keys',
        name: 'admin-api-keys',
        component: () => import('@/presentation/views/admin/ApiKeysView.vue'),
    },
    {
        path: '/admin/routes',
        name: 'admin-routes',
        component: () => import('@/presentation/views/admin/RoutesView.vue'),
    },
    {
        path: '/admin/users',
        name: 'admin-users',
        component: () => import('@/presentation/views/admin/UsersView.vue'),
    },
    {
        path: '/admin/teams',
        name: 'admin-teams',
        component: () => import('@/presentation/views/admin/TeamsView.vue'),
    },
    {
        path: '/admin/tenants',
        name: 'admin-tenants',
        component: () => import('@/presentation/views/admin/TenantsView.vue'),
    },
    // Audit log viewer complet.
    {
        path: '/admin/audit-log',
        name: 'admin-audit-log',
        component: () => import('@/presentation/views/admin/AuditLogView.vue'),
    },
    // Routing v2 — Provider Keys (credentials) + self-service /me.
    {
        path: '/admin/provider-keys',
        name: 'admin-provider-keys',
        component: () => import('@/presentation/views/admin/ProviderKeysView.vue'),
    },
    {
        path: '/me',
        name: 'me',
        component: () => import('@/presentation/views/me/MeView.vue'),
    },
    // Documentation de référence : secrets tokenisés + injections bloquées.
    {
        path: '/docs',
        name: 'docs',
        component: () => import('@/presentation/views/DocsView.vue'),
    },
    {
        path: '/login',
        name: 'login',
        component: () => import('@/presentation/views/LoginView.vue'),
        meta: { public: true },
    },
    {
        path: '/showcase',
        name: 'showcase',
        component: () => import('@/presentation/views/ShowcaseView.vue'),
        meta: { public: true },
    },
    // Fallback : tout chemin inconnu authentifié → home, non-authentifié → login.
    // Le guard requireAuth s'en charge à la prochaine étape, on redirige vers '/'.
    {
        path: '/:pathMatch(.*)*',
        redirect: '/',
    },
]

export const router = createRouter({
    history: createWebHistory(),
    routes,
})

/**
 * requireAuth : guard global. Lit le session store, route en conséquence.
 *
 * Précondition : le bootstrap doit avoir appelé `useSession().hydrate()`
 * avant le premier router.push, donc `status` n'est plus 'unknown' quand
 * le guard tourne. Cf. `infrastructure/bootstrap/main.ts`.
 *
 * Le cas 'unknown' est traité défensivement (redirect /login) — si le
 * boot a foiré on préfère pousser l'user vers le login plutôt que
 * laisser une UI vide ; il pourra retry depuis là.
 */
const requireAuth: NavigationGuardWithThis<undefined> = (to, _from) => {
    const session = useSessionStore()

    if (isPublic(to)) {
        // /login + auth déjà OK : on évite l'écran de login inutile.
        if (to.name === 'login' && session.isAuthenticated) {
            const redirect = readRedirectQuery(to)
            return { path: redirect ?? '/', replace: true }
        }
        return true
    }

    if (session.isAuthenticated) {
        return true
    }

    // Non authentifié (ou status 'unknown' → boot raté) : on pousse
    // vers /login en conservant la destination pour redirection post-login.
    return {
        path: '/login',
        query: { redirect: to.fullPath },
        replace: true,
    }
}

router.beforeEach(requireAuth)

function isPublic(to: RouteLocationNormalized): boolean {
    return Boolean(to.meta?.public)
}

function readRedirectQuery(to: RouteLocationNormalized): string | null {
    const raw = to.query.redirect
    if (typeof raw === 'string' && raw.startsWith('/') && !raw.startsWith('//')) {
        return raw
    }
    return null
}
